import { Timeout } from 'src/types';
import { b64toBlob } from 'src/utils/b64toBlob';

import { client } from './client';
import { EmployeeShortInfo, StatusResponse } from './types';

export interface BookPlaceParams {
  placeId: number;
  dateFrom: string;
  dateTo: string;
  responsible?: string;
  bookData?: {
    guestName?: string;
    carModel: string;
    carNumber: string;
    needPass: boolean;
  };
}

export interface BookingActionResponse {
  accepted: boolean;
  commandId: number;
  rejectedReason?: string;
}

export interface CheckPlaceResponse {
  processed: boolean;
  bookingId: string;
  rejectedReason?: string;
}

export const bookPlace = async (
  params: BookPlaceParams,
): Promise<BookingActionResponse> => {
  try {
    const response = await client.post<BookingActionResponse>(
      '/api/v2/bookings/bookPlace',
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const checkBookPlace = async (
  commandId: number,
): Promise<CheckPlaceResponse> => {
  try {
    const response = await client.get<CheckPlaceResponse>(
      '/api/v2/bookings/checkBookPlace',
      {
        params: {
          commandId,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const checkBookRoomPlace = async (
  commandId: number,
): Promise<CheckPlaceResponse> => {
  try {
    const response = await client.get<CheckPlaceResponse>(
      '/api/v2/bookings/checkBookRoomPlace',
      {
        params: {
          commandId,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const unbookPlace = async (
  bookingId: string,
): Promise<BookingActionResponse> => {
  try {
    const response = await client.post<BookingActionResponse>(
      '/api/v2/bookings/unbookPlace',
      {
        bookingId,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface UnbookRoomPlaceParams {
  bookingId: string;
  placeId: number;
  dateFrom: string;
  dateTo: string;
}

export const unbookRoomPlace = async (
  params: UnbookRoomPlaceParams,
): Promise<BookingActionResponse> => {
  try {
    const response = await client.post<BookingActionResponse>(
      '/api/v2/bookings/unbookRoomPlace',
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface FindEmployeesParams {
  searchText: string;
  itemOffset: number;
  itemLimit: number;
}

export interface FindEmployeesResponse {
  employees: EmployeeShortInfo[];
}

export const findEmployees = async (
  params: FindEmployeesParams,
): Promise<EmployeeShortInfo[]> => {
  try {
    const response = await client.get<FindEmployeesResponse>(
      '/api/v2/employees',
      { params },
    );
    return response.data.employees;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface EmployeeFullInfo {
  employeeNumber: string;
  eMail: string;
  phone: string;
  firstName: string;
  lastName: string;
  middleName: string;
  photo: string;
  positionName: string;
}

export const getEmployeeInfo = async (
  employeeNumber: string,
): Promise<EmployeeFullInfo> => {
  try {
    const response = await client.get<EmployeeFullInfo>(
      `/api/v2/employees/${employeeNumber}`,
    );

    const employee = { ...response.data };

    if (employee.photo) {
      const imageBlob = await b64toBlob(employee.photo);
      employee.photo = URL.createObjectURL(imageBlob);
    }

    return employee;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface CheckBookingParams {
  commandId: number;
  checkBookingIntervalMs?: number;
  maxDurationMs?: number;
  maxDurationExceededError?: string;
}

export const checkBooking = async ({
  commandId,
  checkBookingIntervalMs = 500,
  maxDurationMs = 1000 * 60 * 5,
  maxDurationExceededError = 'Нет ответа от сервера',
}: CheckBookingParams): Promise<CheckPlaceResponse> => {
  return new Promise((resolve, reject) => {
    let requestNumber = 0;
    const base = 2 ** 0.5;
    let refreshTimeout: Timeout | undefined;
    const finishTimeout: Timeout = setTimeout(() => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      reject(maxDurationExceededError);
    }, maxDurationMs);

    const refresh = async (): Promise<void> => {
      try {
        const checkBookPlaceResponse = await checkBookPlace(commandId);
        if (checkBookPlaceResponse.rejectedReason) {
          clearTimeout(finishTimeout);
          reject({ message: checkBookPlaceResponse.rejectedReason });
          return;
        }

        if (checkBookPlaceResponse.processed) {
          clearTimeout(finishTimeout);
          resolve(checkBookPlaceResponse);
          return;
        }

        setTimeout(refresh, checkBookingIntervalMs * base ** requestNumber);
        requestNumber++;
      } catch (e) {
        clearTimeout(finishTimeout);
        reject(e);
      }
    };

    refresh();
  });
};
